// =============================================================================
// SIREPPAR - Selector de Tipo de Documento
// =============================================================================
// Reemplaza al <select> nativo: la lista de tipos es larga y el desplegable
// nativo se salía de pantalla. Mientras no hay tipo elegido, el botón queda
// resaltado (borde ámbar).
//
// Dos decisiones que valen la pena:
//
// 1. BÚSQUEDA. Con ~40 tipos, scrollear para encontrar "Bases de Acción
//    Política" es más lento que escribir "bases". El filtro ignora acentos y
//    mayúsculas, y prioriza los que EMPIEZAN con lo escrito sobre los que solo
//    lo contienen, así "acta" pone primero las actas y no "Constancia de...".
//
// 2. PORTAL. La lista se renderiza en <body> con position:fixed, no dentro del
//    contenedor. Si queda dentro, un modal la recorta y hay que scrollear el
//    modal entero para ver las opciones. Al estar en el body se despliega sobre
//    todo y, si no entra para abajo, se abre para arriba.
//    Costo de esto: el click en la lista es "fuera" del contenedor, así que el
//    cierre por click-outside tiene que mirar TAMBIÉN el panel (listRef), y la
//    posición hay que recalcularla al scrollear (por eso el listener en fase de
//    captura: el scroll que importa suele ser el de un contenedor interno, y ese
//    no burbujea hasta window).
//
// Por eso el campo de búsqueda es el PROPIO disparador (un combobox) y no vive
// dentro del panel: el panel está fuera del <Dialog> de Headless UI, que atrapa
// el foco y se lo devolvería al modal apenas se intentara escribir ahí. Con el
// input en el disparador, el foco nunca sale del modal y el panel solo recibe
// clicks.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import PopoverPortal from './PopoverPortal';
import { normalizarTexto, coincideBusqueda } from '../../utils/busqueda';
import { TODOS_LOS_TIPOS, tipoDocLabels } from '../../utils/tiposDocumento';

interface Props {
  value: string;
  onChange: (tipo: string) => void;
  placeholder?: string;
  // Tipos adicionales a incluir según contexto (p.ej. obligatorios de RECONOCIMIENTO).
  // Se muestran primero y se deduplican contra la lista base.
  tiposExtra?: string[];
  // Allowlist: si se provee, el desplegable ofrece SOLO estos tipos (ignora la lista base).
  tiposPermitidos?: string[];
  // Etiquetas visuales por tipo, solo para esta instancia (p.ej. sufijo "Promotor").
  labelOverrides?: Record<string, string>;
}

const PANEL_MAX_HEIGHT = 320;
const PANEL_MIN_WIDTH = 320;

export default function TipoDocumentoSelect({
  value,
  onChange,
  placeholder = 'Seleccioná un tipo de documento…',
  tiposExtra,
  tiposPermitidos,
  labelOverrides,
}: Props) {
  const etiqueta = (key: string) => labelOverrides?.[key] ?? tipoDocLabels[key] ?? key;
  const [open, setOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resaltado, setResaltado] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const tipos = useMemo(() => (
    tiposPermitidos && tiposPermitidos.length
      ? tiposPermitidos
      : tiposExtra && tiposExtra.length
        ? Array.from(new Set([...tiposExtra, ...TODOS_LOS_TIPOS]))
        : TODOS_LOS_TIPOS
  ), [tiposPermitidos, tiposExtra]);

  // Filtro: primero los que empiezan con lo buscado, después los que lo contienen.
  // Se busca tanto en la etiqueta visible como en la clave del enum, así
  // "PADRON" también encuentra "Padrón de Afiliados (excel)".
  const filtrados = useMemo(() => {
    const q = busqueda.trim();
    if (!q) return tipos;
    const primeraPalabra = normalizarTexto(q).split(/\s+/)[0];
    const empiezan: string[] = [];
    const contienen: string[] = [];
    for (const key of tipos) {
      if (!coincideBusqueda(q, `${etiqueta(key)} ${key}`)) continue;
      const arranca = normalizarTexto(etiqueta(key)).startsWith(primeraPalabra)
        || normalizarTexto(key).startsWith(primeraPalabra);
      (arranca ? empiezan : contienen).push(key);
    }
    return [...empiezan, ...contienen];
  }, [busqueda, tipos, labelOverrides]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Cada vez que cambia el filtro, el resaltado vuelve al primer resultado.
  useEffect(() => { setResaltado(0); }, [busqueda]);

  // Mantiene a la vista la opción resaltada al navegar con las flechas. El panel
  // está en un portal, fuera de este árbol, así que se lo busca por su marca.
  useEffect(() => {
    if (!open) return;
    document.querySelectorAll('[data-portal-dropdown] li')[resaltado]?.scrollIntoView({ block: 'nearest' });
  }, [resaltado, open]);

  const abrir = () => {
    setBusqueda('');
    setResaltado(Math.max(0, tipos.indexOf(value)));
    setOpen(true);
  };

  const elegir = (key: string) => {
    onChange(key);
    setOpen(false);
    setBusqueda('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setResaltado(i => Math.min(i + 1, filtrados.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setResaltado(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      // Enter dentro de un modal no debe disparar el submit/guardar de atrás.
      e.stopPropagation();
      const elegido = filtrados[resaltado];
      if (elegido) elegir(elegido);
    }
  };

  const label = value ? etiqueta(value) : '';

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`w-full flex items-center gap-2 rounded-lg pl-3 pr-2 py-2 ${
          value ? 'border border-gray-300 bg-white' : 'border-2 border-amber-400 bg-amber-50'
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          // Cerrado muestra lo elegido; abierto, lo que se está tipeando.
          value={open ? busqueda : label}
          // Solo se escribe con el panel abierto. No hace falta contemplar el
          // caso cerrado: enfocar el campo ya lo abre, y aceptar el tecleo antes
          // de abrir concatenaría la letra al final de la etiqueta seleccionada.
          onChange={(e) => { if (open) setBusqueda(e.target.value); }}
          onFocus={() => { if (!open) abrir(); }}
          onClick={() => { if (!open) abrir(); }}
          onKeyDown={handleKeyDown}
          // Con el panel abierto el campo se vacía para escribir, así que lo ya
          // elegido se sigue viendo en gris como placeholder.
          placeholder={value ? label : placeholder}
          className="flex-1 min-w-0 text-sm bg-transparent focus:outline-none placeholder:text-gray-500"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => (open ? setOpen(false) : (inputRef.current?.focus(), abrir()))}
          className="flex-shrink-0"
          aria-label={open ? 'Cerrar la lista de tipos' : 'Abrir la lista de tipos'}
        >
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <PopoverPortal anchorRef={containerRef} open={open} onClose={() => setOpen(false)} maxHeight={PANEL_MAX_HEIGHT} minWidth={PANEL_MIN_WIDTH}>
          <ul className="overflow-y-auto py-1">
            {filtrados.length === 0 && (
              <li className="px-3 py-3 text-sm text-gray-400">Ningún tipo coincide con “{busqueda}”.</li>
            )}
            {filtrados.map((key, idx) => (
              <li key={key}>
                <button
                  type="button"
                  onMouseEnter={() => setResaltado(idx)}
                  onClick={() => elegir(key)}
                  className={`w-full text-left px-3 py-2 text-sm ${
                    key === value
                      ? 'bg-blue-50 font-medium text-blue-700'
                      : idx === resaltado
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-700'
                  }`}
                >
                  {etiqueta(key)}
                </button>
              </li>
            ))}
          </ul>
      </PopoverPortal>

    </div>
  );
}
